import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  booleanAttribute,
  numberAttribute,
} from '@angular/core';
import { DsButtonV2Component } from '../buttons/ds-button-v2.component';
import { DsIconComponent } from '../icons/ds-icon.component';
import { DsItemListComponent } from '../lists/ds-item-list.component';
import { DsListComponent } from '../lists/ds-list.component';

export interface DsInputSearchResult {
  id: string;
  label: string;
  ariaLabel?: string;
}

export type DsInputSearchPanelAppearance = 'attached' | 'detached';

@Component({
  selector: 'ds-input-search',
  standalone: true,
  imports: [
    DsButtonV2Component,
    DsIconComponent,
    DsItemListComponent,
    DsListComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      class="ds-input-search"
      [class.ds-input-search--disabled]="disabled"
      [class.ds-input-search--panel-detached]="panelAppearance === 'detached'"
      [attr.aria-disabled]="disabled || null"
      (focusout)="handleFocusOut()"
    >
      @if (label) {
        <label class="ds-input-search__label" [attr.for]="inputId">{{ label }}</label>
      }

      <div
        class="ds-input-search__control"
        [class.ds-input-search__control--expanded]="panelVisible"
      >
        <div class="ds-input-search__field" (click)="focusInput()">
          @if (showPrefix && prefix) {
            <span class="ds-input-search__prefix" aria-hidden="true">{{ prefix }}</span>
          }

          <span class="ds-input-search__search-icon" aria-hidden="true">
            <ds-icon name="Search" size="var(--ds-input-search-icon-size)" />
          </span>

          <input
            #inputControl
            class="ds-input-search__input"
            type="search"
            role="combobox"
            autocomplete="off"
            aria-autocomplete="list"
            [attr.id]="inputId"
            [attr.placeholder]="placeholder"
            [attr.aria-label]="ariaLabel || label || placeholder"
            [attr.aria-expanded]="panelVisible"
            [attr.aria-controls]="panelVisible ? panelId : null"
            [attr.aria-describedby]="showHelpText && helpText ? helpId : null"
            [attr.maxlength]="maxCharacters"
            [value]="value"
            [disabled]="disabled"
            (input)="handleInput($event)"
            (focus)="handleInputFocus($event)"
            (blur)="blurred.emit($event)"
            (keydown.escape)="collapse()"
            (keydown.enter)="submit()"
            (keydown.arrowdown)="focusResult(0, $event)"
            (keydown.arrowup)="focusResult(results.length - 1, $event)"
          />

          @if (showClearButton) {
            <ds-button-v2
              class="ds-input-search__clear"
              [label]="clearLabel"
              emphasis="terciary"
              size="xs"
              (pointerdown)="preserveInputFocus($event)"
              (clicked)="clear($event)"
            />
          }
        </div>

        @if (panelVisible) {
          <div class="ds-input-search__panel" [attr.id]="panelId" aria-live="polite">
            @if (displayResults) {
              <ds-list [border]="false" shadow="none" [borderRadius]="false">
                @for (result of results; track result.id) {
                  <ds-item-list
                    [showIcon]="false"
                    [borderBottom]="!$last"
                    [borderRadius]="true"
                    [interactive]="true"
                    [ariaLabel]="result.ariaLabel || result.label"
                    (pointerdown)="preserveInputFocus($event)"
                    (keydown.arrowdown)="focusNextResult($index, $event)"
                    (keydown.arrowup)="focusPreviousResult($index, $event)"
                    (keydown.escape)="collapseAndFocusInput($event)"
                    (clicked)="selectResult(result)"
                  >
                    <span class="ds-input-search__result-label">{{ result.label }}</span>
                  </ds-item-list>
                }
              </ds-list>
            }

            @if (displayNoResults) {
              <div class="ds-input-search__empty">
                <strong class="ds-input-search__empty-title">{{ emptyTitle }}</strong>
                <span class="ds-input-search__empty-message">{{ emptyMessage }}</span>
              </div>
            }

            @if ((displayResults || displayNoResults) && displaySuggestions) {
              <span class="ds-input-search__divider" aria-hidden="true"></span>
            }

            @if (displaySuggestions) {
              <div class="ds-input-search__suggestions">
                <span class="ds-input-search__suggestions-label">{{ suggestionsLabel }}</span>
                <div class="ds-input-search__suggestion-items">
                  @for (suggestion of suggestions; track suggestion) {
                    <ds-button-v2
                      class="ds-input-search__suggestion"
                      [label]="suggestion"
                      emphasis="secondary"
                      size="xs"
                      (pointerdown)="preserveInputFocus($event)"
                      (clicked)="activateSuggestion(suggestion)"
                    />
                  }
                </div>
              </div>
            }
          </div>
        }
      </div>

      @if (showHelpText || showCounter) {
        <div class="ds-input-search__help-area">
          @if (showHelpText) {
            <span class="ds-input-search__help-text" [attr.id]="helpId">{{ helpText }}</span>
          }
          @if (showCounter) {
            <span class="ds-input-search__counter" aria-live="polite">
              {{ value.length }}/{{ maxCharacters }}
            </span>
          }
        </div>
      }
    </div>
  `,
  styles: [`
    :host {
      display: block;
      min-width: var(--ds-input-search-min-width);
      max-width: var(--ds-input-search-max-width);
    }

    .ds-input-search {
      display: flex;
      flex-direction: column;
      gap: var(--ds-input-search-gap);
      width: 100%;
      min-width: 0;
      color: var(--ds-input-search-value-color);
      font-family: 'Sura Sans', system-ui, sans-serif;
      box-sizing: border-box;
    }

    .ds-input-search--disabled {
      opacity: calc(
        var(--ds-input-search-disabled-opacity) /
        var(--ds-input-search-enabled-opacity)
      );
      pointer-events: none;
    }

    .ds-input-search__label {
      width: 100%;
      color: var(--ds-input-search-label-color);
      font-size: var(--ds-input-search-label-font-size);
      font-weight: var(--ds-input-search-label-font-weight);
      line-height: var(--ds-input-search-label-line-height);
      text-box-trim: trim-both;
      text-box-edge: cap alphabetic;
    }

    .ds-input-search__control {
      width: 100%;
      min-width: 0;
      overflow: hidden;
      border: var(--ds-input-search-border-width) solid var(--ds-input-search-border-color);
      border-radius: var(--ds-input-search-border-radius);
      background: var(--ds-input-search-background);
      box-sizing: border-box;
      transition:
        border-color var(--ds-input-search-transition-duration) ease,
        outline-color var(--ds-input-search-transition-duration) ease;
    }

    .ds-input-search__control:focus-within,
    .ds-input-search__control--expanded {
      outline: var(--ds-focus-stroke) solid var(--ds-focus-color);
      outline-offset: var(--ds-focus-gap);
    }

    .ds-input-search--panel-detached .ds-input-search__control {
      overflow: visible;
      border: 0;
      border-radius: var(--ds-input-search-zero-radius);
      background: var(--ds-input-search-dropdown-background);
    }

    .ds-input-search--panel-detached .ds-input-search__control:focus-within,
    .ds-input-search--panel-detached .ds-input-search__control--expanded {
      outline: 0;
    }

    .ds-input-search__field {
      display: flex;
      align-items: center;
      gap: var(--ds-input-search-icon-gap);
      width: 100%;
      height: var(--ds-input-search-field-height);
      padding: var(--ds-input-search-padding-y) var(--ds-input-search-padding-x);
      background: var(--ds-input-search-background);
      box-sizing: border-box;
      cursor: text;
    }

    .ds-input-search--panel-detached .ds-input-search__field {
      border: var(--ds-input-search-border-width) solid var(--ds-input-search-border-color);
      border-radius: var(--ds-input-search-border-radius);
    }

    .ds-input-search--panel-detached .ds-input-search__field:focus-within {
      outline: var(--ds-focus-stroke) solid var(--ds-focus-color);
      outline-offset: var(--ds-focus-gap);
    }

    .ds-input-search__prefix {
      margin-right: var(--ds-input-search-prefix-gap);
      color: var(--ds-input-search-prefix-color);
      font-size: var(--ds-input-search-field-font-size);
      font-weight: var(--ds-input-search-field-font-weight);
      line-height: var(--ds-input-search-field-line-height);
      white-space: nowrap;
      flex-shrink: 0;
    }

    .ds-input-search__search-icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: var(--ds-input-search-icon-size);
      height: var(--ds-input-search-icon-size);
      color: var(--ds-input-search-icon-color);
      flex: 0 0 var(--ds-input-search-icon-size);
      overflow: hidden;
    }

    .ds-input-search__input {
      flex: 1 1 0;
      min-width: 0;
      width: 100%;
      height: 100%;
      margin: 0;
      padding: 0;
      border: 0;
      outline: 0;
      appearance: none;
      background: transparent;
      color: var(--ds-input-search-value-color);
      caret-color: var(--ds-input-search-cursor-color);
      font: inherit;
      font-size: var(--ds-input-search-field-font-size);
      font-weight: var(--ds-input-search-field-font-weight);
      line-height: var(--ds-input-search-field-line-height);
      box-sizing: border-box;
    }

    .ds-input-search__input::-webkit-search-cancel-button {
      appearance: none;
    }

    .ds-input-search__input::placeholder {
      color: var(--ds-input-search-placeholder-color);
      opacity: 1;
    }

    .ds-input-search__clear {
      display: inline-flex;
      flex: 0 0 auto;
    }

    .ds-input-search__panel {
      display: flex;
      flex-direction: column;
      gap: var(--ds-input-search-dropdown-section-gap);
      width: 100%;
      padding: var(--ds-input-search-dropdown-padding);
      border-top: var(--ds-input-search-border-width) solid var(--ds-input-search-dropdown-border-color);
      background: var(--ds-input-search-dropdown-background);
      box-sizing: border-box;
    }

    .ds-input-search--panel-detached .ds-input-search__panel {
      padding: var(--ds-input-search-dropdown-section-gap) 0 0;
      border-top: 0;
    }

    .ds-input-search__result-label {
      min-width: 0;
      overflow: hidden;
      font-size: var(--ds-input-search-result-font-size);
      font-weight: var(--ds-input-search-result-font-weight);
      line-height: var(--ds-input-search-result-line-height);
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .ds-input-search__empty {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--ds-input-search-empty-text-gap);
      width: 100%;
      text-align: center;
    }

    .ds-input-search__empty-title {
      width: 100%;
      color: var(--ds-input-search-empty-title-color);
      font-size: var(--ds-input-search-empty-title-font-size);
      font-weight: var(--ds-input-search-empty-title-font-weight);
      line-height: var(--ds-input-search-empty-title-line-height);
    }

    .ds-input-search__empty-message {
      width: 100%;
      color: var(--ds-input-search-empty-message-color);
      font-size: var(--ds-input-search-empty-message-font-size);
      font-weight: var(--ds-input-search-empty-message-font-weight);
      line-height: var(--ds-input-search-empty-message-line-height);
    }

    .ds-input-search__divider {
      display: block;
      width: 100%;
      height: var(--ds-input-search-border-width);
      background: var(--ds-input-search-divider-color);
      flex: 0 0 var(--ds-input-search-border-width);
    }

    .ds-input-search__suggestions {
      display: flex;
      flex-direction: column;
      gap: var(--ds-input-search-suggestions-gap);
      width: 100%;
    }

    .ds-input-search__suggestions-label {
      width: 100%;
      color: var(--ds-input-search-suggestions-label-color);
      font-size: var(--ds-input-search-suggestions-label-font-size);
      font-weight: var(--ds-input-search-suggestions-label-font-weight);
      line-height: var(--ds-input-search-suggestions-label-line-height);
    }

    .ds-input-search__suggestion-items {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: var(--ds-input-search-suggestion-items-gap);
      width: 100%;
    }

    .ds-input-search__help-area {
      display: flex;
      align-items: flex-start;
      gap: var(--ds-input-search-help-gap);
      width: 100%;
    }

    .ds-input-search__help-text,
    .ds-input-search__counter {
      color: var(--ds-input-search-help-color);
      font-size: var(--ds-input-search-help-font-size);
      font-weight: var(--ds-input-search-help-font-weight);
      line-height: var(--ds-input-search-help-line-height);
    }

    .ds-input-search__help-text {
      flex: 1 1 0;
      min-width: 0;
    }

    .ds-input-search__counter {
      margin-left: auto;
      color: var(--ds-input-search-counter-color);
      white-space: nowrap;
      flex-shrink: 0;
    }
  `],
})
export class DsInputSearchComponent {
  private static counter = 0;

  @ViewChild('inputControl') private inputControl?: ElementRef<HTMLInputElement>;

  readonly inputId = `ds-input-search-${++DsInputSearchComponent.counter}`;
  readonly panelId = `${this.inputId}-panel`;
  readonly helpId = `${this.inputId}-help`;

  @Input() value = '';
  @Input() label = 'Búsqueda';
  @Input() ariaLabel = '';
  @Input() placeholder = '¿Qué estás buscando?';
  @Input() prefix = '';
  @Input({ transform: booleanAttribute }) showPrefix = false;
  @Input() suggestions: string[] = [];
  @Input() results: DsInputSearchResult[] = [];
  @Input({ transform: numberAttribute }) minCharacters = 1;
  @Input() showNoResults: boolean | null = null;
  @Input() helpText = '';
  @Input({ transform: booleanAttribute }) showHelpText = false;
  @Input() maxCharacters: number | null = null;
  @Input({ transform: booleanAttribute }) showMaxCharacters = true;
  @Input({ transform: booleanAttribute }) disabled = false;
  @Input({ transform: booleanAttribute }) expanded = false;
  @Input({ transform: booleanAttribute }) alwaysShowPanel = false;
  @Input() panelAppearance: DsInputSearchPanelAppearance = 'attached';
  @Input() clearLabel = 'Borrar';
  @Input() suggestionsLabel = 'Sugerencias';
  @Input() emptyTitle = 'No encontramos resultados';
  @Input() emptyMessage = 'Por favor, intenta buscar de otra forma';

  @Output() valueChange = new EventEmitter<string>();
  @Output() expandedChange = new EventEmitter<boolean>();
  @Output() cleared = new EventEmitter<void>();
  @Output() suggestionSelected = new EventEmitter<string>();
  @Output() resultSelected = new EventEmitter<DsInputSearchResult>();
  @Output() submitted = new EventEmitter<string>();
  @Output() focused = new EventEmitter<FocusEvent>();
  @Output() blurred = new EventEmitter<FocusEvent>();

  constructor(private readonly host: ElementRef<HTMLElement>) {}

  get hasMinimumCharacters(): boolean {
    return this.value.length >= Math.max(1, this.minCharacters);
  }

  get displayResults(): boolean {
    return this.hasMinimumCharacters && this.results.length > 0;
  }

  get displayNoResults(): boolean {
    if (!this.hasMinimumCharacters || this.results.length) return false;
    return this.showNoResults ?? true;
  }

  get displaySuggestions(): boolean {
    return this.suggestions.length > 0 &&
      (!this.hasMinimumCharacters || this.displayNoResults);
  }

  get panelVisible(): boolean {
    return !this.disabled && (this.expanded || this.alwaysShowPanel) &&
      (this.displayResults || this.displayNoResults || this.displaySuggestions);
  }

  get showClearButton(): boolean {
    return !this.disabled && (this.expanded || this.alwaysShowPanel) && this.value.length > 0;
  }

  get showCounter(): boolean {
    return this.maxCharacters !== null && this.showMaxCharacters;
  }

  handleInput(event: Event): void {
    if (this.disabled) return;
    const input = event.target as HTMLInputElement;
    const nextValue = this.maxCharacters === null
      ? input.value
      : input.value.slice(0, this.maxCharacters);

    if (input.value !== nextValue) input.value = nextValue;
    this.value = nextValue;
    this.valueChange.emit(nextValue);
    this.setExpanded(true);
  }

  handleInputFocus(event: FocusEvent): void {
    if (this.disabled) return;
    this.setExpanded(true);
    this.focused.emit(event);
  }

  handleFocusOut(): void {
    queueMicrotask(() => {
      const activeElement = this.host.nativeElement.ownerDocument.activeElement;
      if (!activeElement || !this.host.nativeElement.contains(activeElement)) {
        this.setExpanded(false);
      }
    });
  }

  focusInput(): void {
    if (this.disabled) return;
    this.setExpanded(true);
    this.inputControl?.nativeElement.focus();
  }

  preserveInputFocus(event: PointerEvent): void {
    event.preventDefault();
  }

  focusResult(index: number, event?: Event): void {
    if (this.disabled || !this.panelVisible || !this.displayResults) return;
    event?.preventDefault();

    const resultItems = this.host.nativeElement.querySelectorAll<HTMLElement>(
      'ds-item-list .ds-item-list',
    );
    resultItems.item(index)?.focus();
  }

  focusNextResult(index: number, event: Event): void {
    const nextIndex = index >= this.results.length - 1 ? 0 : index + 1;
    this.focusResult(nextIndex, event);
  }

  focusPreviousResult(index: number, event: Event): void {
    event.preventDefault();
    if (index === 0) {
      this.inputControl?.nativeElement.focus();
      return;
    }
    this.focusResult(index - 1);
  }

  collapseAndFocusInput(event: Event): void {
    event.preventDefault();
    this.inputControl?.nativeElement.focus();
    this.setExpanded(false);
  }

  clear(event?: MouseEvent): void {
    if (this.disabled) return;
    event?.preventDefault();
    this.value = '';
    this.valueChange.emit('');
    this.cleared.emit();
    this.setExpanded(true);
    queueMicrotask(() => this.focusInput());
  }

  activateSuggestion(suggestion: string): void {
    if (this.disabled) return;
    this.suggestionSelected.emit(suggestion);
    this.setExpanded(false);
  }

  selectResult(result: DsInputSearchResult): void {
    if (this.disabled) return;
    this.resultSelected.emit(result);
    this.setExpanded(false);
  }

  submit(): void {
    if (this.disabled) return;
    this.submitted.emit(this.value);
    this.setExpanded(false);
  }

  collapse(): void {
    this.setExpanded(false);
  }

  private setExpanded(expanded: boolean): void {
    if (this.expanded === expanded) return;
    this.expanded = expanded;
    this.expandedChange.emit(expanded);
  }
}
